import fetch from "cross-fetch";
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  DefaultContext,
  from,
  InMemoryCache,
  MutationOptions,
  OperationVariables,
  QueryOptions,
  TypedDocumentNode,
} from "@apollo/client/core";
import { AuthenticationRepository } from "@/framework/repositories/authenticationRepository";
import { err, ok, type Result } from "neverthrow";
import { setContext } from "@apollo/client/link/context";
import store from "@/store";
import CustomError from "@/framework/errors/CustomError";
import GraphqlApiError from "./GraphqlApiError";
import { z } from "zod";
import { IValidationService } from "@/framework/validation/IValidationService";
import { ValidationService } from "@/framework/validation/ValidationService";

/**
 * Wraps the GraphQL Apollo client with some convenience functionality.
 *
 * - Generating the Apollo client and adding the authentication token
 * - Typing
 * - Basic error handling
 */
export interface IGraphqlApi {
  /**
   * Sends a GraphQL query to the server.
   *
   * Supported inputs:
   * - GraphQL string
   * - DocumentNode (Gql)
   * - TypedDocumentNode
   *
   * @param options GraphQL QueryOptions (query, variables, context etc...)
   */
  query<T = any, TVariables = OperationVariables>(
    options: QueryOptions<TVariables, T>
  ): Promise<Result<T, GraphqlApiError>>;

  /**
   * Helper to send a GraphQL query to the server by using the `TypedDocument`
   * provided by the generated `graphql.ts` file.
   *
   * @param typeDoc Query from the TypedDocument `graphql.ts` types.
   * @param variables Query variables
   * @param noCache
   */
  runQuery<T = any, V = { [key: string]: any }>(
    typeDoc: TypedDocumentNode<T, V>,
    variables?: any,
    noCache?: boolean
  ): Promise<Result<T, GraphqlApiError>>;

  /**
   * Extension of runQuery that also includes zod validation
   * As such, requires a zod schema
   *
   * @param typeDoc Query from the TypedDocument `graphql.ts` types.
   * @param validationSchema Zod type to check against
   * @param variables Query variables
   * @param noCache
   */
  runQueryAndValidate<SourceType, ResultType>(
    typeDoc: TypedDocumentNode<SourceType>,
    validationSchema: z.ZodSchema,
    variables: any,
    noCache?: boolean
  ): Promise<Result<ResultType, GraphqlApiError>>;

  /**
   * Sends a GraphQL mutation to the server.
   *
   * Supported inputs:
   * - GraphQL string
   * - DocumentNode (Gql)
   * - TypedDocumentNode
   *
   * @param options GraphQL QueryOptions (mutation, variables, context etc...)
   */
  mutate<TData = any, TVariables = OperationVariables, TContext = DefaultContext>(
    options: MutationOptions<TData, TVariables, TContext>
  ): Promise<Result<TData | null, GraphqlApiError>>;

  /**
   * Helper to send a GraphQL mutation to the server by using the `TypedDocument`
   * provided by the generated `graphql.ts` file.
   *
   * @param typeDoc Mutation from the TypedDocument `graphql.ts` types.
   * @param variables Mutation variables
   */
  runMutation<T = any, V = { [key: string]: any }>(
    typeDoc: TypedDocumentNode<T, V>,
    variables?: any
  ): Promise<Result<T | null, GraphqlApiError>>;

  /**
   * Extension of runMutation that also includes zod validation
   * As such, requires a zod schema
   *
   * @param typeDoc Query from the TypedDocument `graphql.ts` types.
   * @param validationSchema Zod type to check against
   * @param variables Query variables
   */
  runMutationAndValidate<SourceType, ResultType>(
    typeDoc: TypedDocumentNode<SourceType>,
    validationSchema: z.ZodSchema,
    variables?: any
    // ): Promise<Result<ResultType | null, GraphqlApiError>>;
  ): Promise<Result<ResultType, GraphqlApiError>>;
}

export class GraphqlApi implements IGraphqlApi {
  private readonly client: ApolloClient<any>;
  private readonly httpLink: ApolloLink;
  private readonly authMiddleware: any;

  private validation: IValidationService;

  constructor(
    isBot: boolean = false,
    // Cache implementation
    cache = new InMemoryCache(),
    validation: IValidationService = new ValidationService()
  ) {
    this.validation = validation;
    // You should use an absolute BE URL here. Modify it for using it locally
    this.httpLink = createHttpLink({
      uri: isBot
        ? `${process.env.VUE_APP_KTZN_BOT_BE_ENDPOINT}/secure/graphql`
        : `${process.env.VUE_APP_KTZN_BE_ENDPOINT}/secure/graphql`,
      fetch,
    });

    /**
     * Refresh token
     */
    const refreshToken = async () => {
      let token = store.getters.accessToken;

      if ((!token || token.length == 0) && !store.getters.isUserLoggedOut) {
        return err(
          new CustomError(
            CustomError.Type.graphqlServerError,
            "",
            "Error occurred in ApolloGraphQLService - token does not exist"
          )
        );
      }

      // this causes issues down the road since token can STILL be null, even if it passed the above check
      // confirm behavior later since just stopping execustion causes infinite redirects
      const base64Url = token.split(".")[1];
      if (!base64Url && !store.getters.isUserLoggedOut) {
        return err(
          new CustomError(
            CustomError.Type.graphqlServerError,
            "",
            "Error occurred in ApolloGraphQLService - token is not base64Url"
          )
        );
      }

      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );

      const { exp } = JSON.parse(jsonPayload);
      let expired = Date.now() >= exp * 1000;

      if (expired) {
        await new AuthenticationRepository().authRefresh();
      }

      token = store.getters.accessToken;

      return ok(token);
    };

    // Add the authorization to the headers
    this.authMiddleware = setContext(async () => {
      const refreshedToken = await refreshToken();

      if (refreshedToken.isErr()) {
        await new AuthenticationRepository().logoutUserForcefully(false);
        return;
      }

      return {
        headers: {
          Authorization: refreshedToken.value ? `Bearer ${refreshedToken.value}` : "",
        },
      };
    });

    // Create the apollo client
    this.client = new ApolloClient({
      link: from([this.authMiddleware, this.httpLink]),
      cache,
    });
  }

  query = async <T = any, TVariables = OperationVariables>(
    options: QueryOptions<TVariables, T>
  ): Promise<Result<T, GraphqlApiError>> => {
    try {
      //@ts-ignore
      const res = await this.client.query(options);

      if (res && res.data !== undefined && res.data !== null) {
        return ok(res.data);
      } else {
        let operationName = "";
        const variables = options.variables;
        if (options.query.definitions) {
          options.query.definitions.forEach((definition) => {
            if (definition.kind === "OperationDefinition" && definition.name) {
              operationName = definition.name.value;
            }
          });
        }
        const error = new GraphqlApiError(
          GraphqlApiError.Type.graphqlFailed,
          undefined,
          undefined,
          undefined,
          operationName,
          variables
        );
        return err(error);
      }
    } catch (unknownError) {
      const error = GraphqlApiError.create(unknownError, options, null);
      return err(error);
    }
  };

  runQuery = async <T = any, V = { [key: string]: any }>(
    typeDoc: TypedDocumentNode<T, V>,
    variables: any = {},
    noCache: boolean = false
  ): Promise<Result<T, GraphqlApiError>> => {
    if (noCache) {
      return await this.query({
        query: typeDoc,
        variables: variables ? variables : {},
        fetchPolicy: "network-only",
      });
    } else {
      return await this.query({
        query: typeDoc,
        variables: variables ? variables : {},
      });
    }
  };

  runQueryAndValidate = async <SourceType, ResultType>(
    typeDoc: TypedDocumentNode<SourceType>,
    validationSchema: z.ZodSchema,
    variables: any = {},
    noCache: boolean = false
  ): Promise<Result<ResultType, GraphqlApiError>> => {
    const res = await this.runQuery(typeDoc, variables, noCache);

    // there was an error
    if (res.isErr()) return err(res.error);
    // there was no data or no response
    if (!res || (res.isOk() && (res.value == null || res.value == undefined)))
      return err(
        new GraphqlApiError(GraphqlApiError.Type.noData, undefined, undefined, "No data returned in response")
      );

    // @ts-ignore
    const resultKey: string = typeDoc.definitions[0].name.value as string;
    // console.error("query resultKey", resultKey); // uncomment this to see the name of the request for easier debugging
    // because somehow, sometimes the result key is capitalized, but the object key is lower case, causing undefined results
    const graphqlResultValue =
      (res as any).value[resultKey] || (res as any).value[resultKey.charAt(0).toLowerCase() + resultKey.slice(1)];

    if (!graphqlResultValue)
      return err(new GraphqlApiError(GraphqlApiError.Type.noData, undefined, undefined, resultKey));

    // Validate and convert server response to expected result
    const validatedResult = this.validation.convert<SourceType, typeof validationSchema, ResultType>(
      graphqlResultValue,
      validationSchema
    );
    if (validatedResult.isErr()) {
      return err(GraphqlApiError.createFromZod(validatedResult.error, resultKey));
    }
    return ok(validatedResult.value);
  };

  mutate = async <TData = any, TVariables = OperationVariables, TContext = DefaultContext>(
    options: MutationOptions<TData, TVariables, TContext>
  ): Promise<Result<TData | null, GraphqlApiError>> => {
    try {
      //@ts-ignore
      const res = await this.client.mutate(options);

      if (res.data !== undefined && res.data !== null) {
        return ok(res.data);
      } else {
        let operationName = "";
        const variables = options.variables;
        if (options.mutation.definitions) {
          options.mutation.definitions.forEach((definition) => {
            if (definition.kind === "OperationDefinition" && definition.name) {
              operationName = definition.name.value;
            }
          });
        }
        const error = new GraphqlApiError(
          GraphqlApiError.Type.graphqlFailed,
          undefined,
          undefined,
          undefined,
          operationName,
          variables
        );
        return err(error);
      }
    } catch (unknownError) {
      const error = GraphqlApiError.create(unknownError, null, options);
      return err(error);
    }
  };

  runMutation = async <T = any, V = { [key: string]: any }>(
    typeDoc: TypedDocumentNode<T, V>,
    variables?: any
  ): Promise<Result<T | null, GraphqlApiError>> => {
    return await this.mutate({
      mutation: typeDoc,
      variables: variables ? variables : {},
    });
  };

  runMutationAndValidate = async <SourceType, ResultType>(
    typeDoc: TypedDocumentNode<SourceType>,
    validationSchema: z.ZodSchema,
    variables?: any
    // ): Promise<Result<ResultType | null, GraphqlApiError>> => {
  ): Promise<Result<ResultType, GraphqlApiError>> => {
    const res = await this.runMutation(typeDoc, variables);

    // there was an error
    if (res.isErr()) return err(res.error);
    // there was no data or no response
    if (!res || (res.isOk() && (res.value == null || res.value == undefined)))
      return err(
        new GraphqlApiError(GraphqlApiError.Type.noData, undefined, undefined, "No data returned in response")
      );

    // @ts-ignore
    const resultKey: string = typeDoc.definitions[0].name.value as string;
    // console.error("mutation resultKey", resultKey); // uncomment this to see the name of the request for easier debugging
    const graphqlResultValue = (res as any).value[resultKey];

    if (!graphqlResultValue)
      return err(new GraphqlApiError(GraphqlApiError.Type.noData, undefined, undefined, resultKey));

    // Validate and convert server response to expected result
    const validatedResult = this.validation.convert<SourceType, typeof validationSchema, ResultType>(
      graphqlResultValue,
      validationSchema
    );
    if (validatedResult.isErr()) {
      return err(GraphqlApiError.createFromZod(validatedResult.error, resultKey));
    }
    return ok(validatedResult.value);
  };
}
